// src/services/notificationService.js
import api from '../api/api'

/**
 * Notification Service
 * Handles all notification-related API calls
 */

// ============ NOTIFICATIONS ============

/**
 * Get paginated list of notifications
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {string} params.notification_type - Filter by type (e.g., 'order_delivered', 'order_confirmed')
 * @param {boolean} params.is_read - Filter by read status
 * @returns {Promise<Object>} - Paginated notifications response
 */
export const getNotifications = async (params = {}) => {
  const response = await api.get('/notifications/notifications/', { params })
  return response.data
}

/**
 * Get unread notifications count
 * @returns {Promise<Object>} - { unread_count: number }
 */
export const getUnreadCount = async () => {
  const response = await api.get('/notifications/notifications/unread_count/')
  return response.data
}

/**
 * Get dashboard statistics for notifications
 * @returns {Promise<Object>} - Dashboard stats object
 */
export const getDashboardStats = async () => {
  const response = await api.get('/notifications/notifications/dashboard_stats/')
  return response.data
}

/**
 * Mark a notification as read
 * @param {string} notificationId - UUID of the notification
 * @returns {Promise<Object>} - Success response { message: "Notification marked as read" }
 */
export const markAsRead = async notificationId => {
  const response = await api.patch(`/notifications/notifications/${notificationId}/mark_as_read/`)
  return response.data
}

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} - Success response { message: "X notifications marked as read" }
 */
export const markAllAsRead = async () => {
  const response = await api.patch('/notifications/notifications/mark_all_as_read/')
  return response.data
}

/**
 * Delete a notification
 * @param {string} notificationId - UUID of the notification
 * @returns {Promise<void>}
 */
export const deleteNotification = async notificationId => {
  await api.delete(`/notifications/notifications/${notificationId}/`)
}

// ============ DEVICES ============

/**
 * Get list of registered devices
 * @returns {Promise<Object>} - Paginated devices response
 */
export const getDevices = async () => {
  const response = await api.get('/notifications/devices/')
  return response.data
}

/**
 * Register a new device for push notifications
 * @param {Object} deviceData - Device information
 * @param {string} deviceData.device_token - FCM/APNs token
 * @param {string} deviceData.device_type - 'android' | 'ios' | 'web'
 * @param {string} deviceData.device_name - Optional device name
 * @returns {Promise<Object>} - Created device object
 */
export const registerDevice = async deviceData => {
  const response = await api.post('/notifications/devices/', deviceData)
  return response.data
}

/**
 * Update a device
 * @param {number} deviceId - Device ID
 * @param {Object} deviceData - Updated device data
 * @returns {Promise<Object>} - Updated device object
 */
export const updateDevice = async (deviceId, deviceData) => {
  const response = await api.patch(`/notifications/devices/${deviceId}/`, deviceData)
  return response.data
}

/**
 * Delete/unregister a device
 * @param {number} deviceId - Device ID
 * @returns {Promise<void>}
 */
export const deleteDevice = async deviceId => {
  await api.delete(`/notifications/devices/${deviceId}/`)
}

/**
 * Deactivate a device (soft delete)
 * @param {number} deviceId - Device ID
 * @returns {Promise<Object>} - Updated device object
 */
export const deactivateDevice = async deviceId => {
  const response = await api.post(`/notifications/devices/${deviceId}/deactivate/`)
  return response.data
}

// ============ PREFERENCES ============

/**
 * Get notification preferences
 * @returns {Promise<Object>} - Preferences object
 */
export const getPreferences = async () => {
  const response = await api.get('/notifications/preferences/')
  return response.data
}

/**
 * Update notification preferences
 * @param {Object} preferences - Updated preferences
 * @param {boolean} preferences.push_enabled - Enable push notifications
 * @param {boolean} preferences.email_enabled - Enable email notifications
 * @param {boolean} preferences.sms_enabled - Enable SMS notifications
 * @param {boolean} preferences.websocket_enabled - Enable WebSocket notifications
 * @param {boolean} preferences.order_updates - Enable order update notifications
 * @param {boolean} preferences.delivery_updates - Enable delivery update notifications
 * @param {boolean} preferences.payment_updates - Enable payment update notifications
 * @param {boolean} preferences.promotional - Enable promotional notifications
 * @param {boolean} preferences.system_alerts - Enable system alert notifications
 * @param {boolean} preferences.quiet_hours_enabled - Enable quiet hours
 * @param {string|null} preferences.quiet_start_time - Quiet hours start time (HH:MM:SS)
 * @param {string|null} preferences.quiet_end_time - Quiet hours end time (HH:MM:SS)
 * @returns {Promise<Object>} - Updated preferences object
 */
export const updatePreferences = async preferences => {
  // Try PATCH first, fallback to PUT if it fails
  try {
    const response = await api.patch('/notifications/preferences/', preferences)
    return response.data
  } catch (error) {
    if (error.response?.status === 405) {
      // Fallback to PUT if PATCH not allowed
      const response = await api.put('/notifications/preferences/', preferences)
      return response.data
    }
    throw error
  }
}

// ============ NOTIFICATION TYPES ============

export const NOTIFICATION_TYPES = {
  ORDER_CREATED: 'order_created',
  ORDER_CONFIRMED: 'order_confirmed',
  ORDER_PREPARING: 'order_preparing',
  ORDER_READY: 'order_ready',
  ORDER_AVAILABLE: 'order_available',
  ORDER_ASSIGNED: 'order_assigned',
  DRIVER_EN_ROUTE: 'driver_en_route',
  DRIVER_ARRIVED: 'driver_arrived',
  ORDER_PICKED_UP: 'order_picked_up',
  ORDER_IN_TRANSIT: 'order_in_transit',
  ORDER_DELIVERED: 'order_delivered',
  ORDER_CANCELLED: 'order_cancelled',
  PAYMENT_RECEIVED: 'payment_received',
  RATING_RECEIVED: 'rating_received',
  SYSTEM_ALERT: 'system_alert',
  LOCATION_UPDATE: 'location_update',
  STATUS_UPDATE: 'status_update'
}

export const NOTIFICATION_TYPE_LABELS = {
  [NOTIFICATION_TYPES.ORDER_CREATED]: 'Order Created',
  [NOTIFICATION_TYPES.ORDER_CONFIRMED]: 'Order Confirmed',
  [NOTIFICATION_TYPES.ORDER_PREPARING]: 'Order Being Prepared',
  [NOTIFICATION_TYPES.ORDER_READY]: 'Order Ready for Pickup',
  [NOTIFICATION_TYPES.ORDER_AVAILABLE]: 'Order Available for Drivers',
  [NOTIFICATION_TYPES.ORDER_ASSIGNED]: 'Order Assigned',
  [NOTIFICATION_TYPES.DRIVER_EN_ROUTE]: 'Driver En Route',
  [NOTIFICATION_TYPES.DRIVER_ARRIVED]: 'Driver Arrived',
  [NOTIFICATION_TYPES.ORDER_PICKED_UP]: 'Order Picked Up',
  [NOTIFICATION_TYPES.ORDER_IN_TRANSIT]: 'Order In Transit',
  [NOTIFICATION_TYPES.ORDER_DELIVERED]: 'Order Delivered',
  [NOTIFICATION_TYPES.ORDER_CANCELLED]: 'Order Cancelled',
  [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: 'Payment Received',
  [NOTIFICATION_TYPES.RATING_RECEIVED]: 'Rating Received',
  [NOTIFICATION_TYPES.SYSTEM_ALERT]: 'System Alert',
  [NOTIFICATION_TYPES.LOCATION_UPDATE]: 'Location Update',
  [NOTIFICATION_TYPES.STATUS_UPDATE]: 'Status Update'
}

export const PRIORITY_LEVELS = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
}

export const PRIORITY_LABELS = {
  [PRIORITY_LEVELS.LOW]: 'Low',
  [PRIORITY_LEVELS.NORMAL]: 'Normal',
  [PRIORITY_LEVELS.HIGH]: 'High',
  [PRIORITY_LEVELS.URGENT]: 'Urgent'
}

export const CHANNEL_TYPES = {
  PUSH: 'push',
  EMAIL: 'email',
  SMS: 'sms',
  WEBSOCKET: 'websocket'
}

export const CHANNEL_LABELS = {
  [CHANNEL_TYPES.PUSH]: 'Push Notification',
  [CHANNEL_TYPES.EMAIL]: 'Email',
  [CHANNEL_TYPES.SMS]: 'SMS',
  [CHANNEL_TYPES.WEBSOCKET]: 'Real-time'
}

export const DEVICE_TYPES = {
  IOS: 'ios',
  ANDROID: 'android',
  WEB: 'web'
}

export const DEVICE_LABELS = {
  [DEVICE_TYPES.IOS]: 'iOS',
  [DEVICE_TYPES.ANDROID]: 'Android',
  [DEVICE_TYPES.WEB]: 'Web Browser'
}

export const UPDATE_TYPES = {
  LOCATION: 'location',
  STATUS: 'status',
  ETA: 'eta',
  MESSAGE: 'message',
  ALERT: 'alert'
}

export const UPDATE_LABELS = {
  [UPDATE_TYPES.LOCATION]: 'Location Update',
  [UPDATE_TYPES.STATUS]: 'Status Update',
  [UPDATE_TYPES.ETA]: 'ETA Update',
  [UPDATE_TYPES.MESSAGE]: 'Message',
  [UPDATE_TYPES.ALERT]: 'Alert'
}

// ============ FCM DELIVERY STATUS (for push notifications) ============

export const DELIVERY_STATUS = {
  CONFIRMED: 'confirmed',
  PREPARING: 'preparing',
  PICKED_UP: 'picked_up',
  ON_THE_WAY: 'on_the_way',
  NEARBY: 'nearby',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
}

export const DELIVERY_STATUS_LABELS = {
  [DELIVERY_STATUS.CONFIRMED]: 'Order Confirmed',
  [DELIVERY_STATUS.PREPARING]: 'Preparing Order',
  [DELIVERY_STATUS.PICKED_UP]: 'Order Picked Up',
  [DELIVERY_STATUS.ON_THE_WAY]: 'On The Way',
  [DELIVERY_STATUS.NEARBY]: 'Almost There',
  [DELIVERY_STATUS.DELIVERED]: 'Delivered',
  [DELIVERY_STATUS.CANCELLED]: 'Cancelled'
}

export const DELIVERY_STATUS_PROGRESS = {
  [DELIVERY_STATUS.CONFIRMED]: 10,
  [DELIVERY_STATUS.PREPARING]: 30,
  [DELIVERY_STATUS.PICKED_UP]: 50,
  [DELIVERY_STATUS.ON_THE_WAY]: 70,
  [DELIVERY_STATUS.NEARBY]: 90,
  [DELIVERY_STATUS.DELIVERED]: 100,
  [DELIVERY_STATUS.CANCELLED]: 0
}

export const DELIVERY_STATUS_ICONS = {
  [DELIVERY_STATUS.CONFIRMED]: 'ph-check-circle',
  [DELIVERY_STATUS.PREPARING]: 'ph-cooking-pot',
  [DELIVERY_STATUS.PICKED_UP]: 'ph-package',
  [DELIVERY_STATUS.ON_THE_WAY]: 'ph-truck',
  [DELIVERY_STATUS.NEARBY]: 'ph-map-pin',
  [DELIVERY_STATUS.DELIVERED]: 'ph-check-fat',
  [DELIVERY_STATUS.CANCELLED]: 'ph-x-circle'
}

export const DELIVERY_STATUS_COLORS = {
  [DELIVERY_STATUS.CONFIRMED]: '#27ae60',
  [DELIVERY_STATUS.PREPARING]: '#f39c12',
  [DELIVERY_STATUS.PICKED_UP]: '#9b59b6',
  [DELIVERY_STATUS.ON_THE_WAY]: '#3498db',
  [DELIVERY_STATUS.NEARBY]: '#1abc9c',
  [DELIVERY_STATUS.DELIVERED]: '#2ecc71',
  [DELIVERY_STATUS.CANCELLED]: '#e74c3c'
}

// FCM Message Types
export const FCM_MESSAGE_TYPES = {
  DELIVERY_UPDATE: 'delivery_update',
  PROMO: 'promo',
  GENERAL: 'general'
}

/**
 * Get delivery status info for FCM push notification
 * @param {string} status - Delivery status from FCM
 * @returns {Object} - Status info with label, progress, icon, color
 */
export const getDeliveryStatusInfo = status => {
  return {
    status,
    label: DELIVERY_STATUS_LABELS[status] || 'Unknown',
    progress: DELIVERY_STATUS_PROGRESS[status] || 0,
    icon: DELIVERY_STATUS_ICONS[status] || 'ph-question',
    color: DELIVERY_STATUS_COLORS[status] || '#7f8c8d'
  }
}

/**
 * Get icon for notification type
 * @param {string} type - Notification type
 * @returns {string} - Icon class name
 */
export const getNotificationIcon = type => {
  const icons = {
    [NOTIFICATION_TYPES.ORDER_CREATED]: 'ph-shopping-cart',
    [NOTIFICATION_TYPES.ORDER_CONFIRMED]: 'ph-check-circle',
    [NOTIFICATION_TYPES.ORDER_PREPARING]: 'ph-cooking-pot',
    [NOTIFICATION_TYPES.ORDER_READY]: 'ph-package',
    [NOTIFICATION_TYPES.ORDER_AVAILABLE]: 'ph-broadcast',
    [NOTIFICATION_TYPES.ORDER_ASSIGNED]: 'ph-user-check',
    [NOTIFICATION_TYPES.DRIVER_EN_ROUTE]: 'ph-path',
    [NOTIFICATION_TYPES.DRIVER_ARRIVED]: 'ph-map-pin',
    [NOTIFICATION_TYPES.ORDER_PICKED_UP]: 'ph-car',
    [NOTIFICATION_TYPES.ORDER_IN_TRANSIT]: 'ph-truck',
    [NOTIFICATION_TYPES.ORDER_DELIVERED]: 'ph-check-fat',
    [NOTIFICATION_TYPES.ORDER_CANCELLED]: 'ph-x-circle',
    [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: 'ph-credit-card',
    [NOTIFICATION_TYPES.RATING_RECEIVED]: 'ph-star',
    [NOTIFICATION_TYPES.SYSTEM_ALERT]: 'ph-bell',
    [NOTIFICATION_TYPES.LOCATION_UPDATE]: 'ph-map-pin-line',
    [NOTIFICATION_TYPES.STATUS_UPDATE]: 'ph-info'
  }
  return icons[type] || 'ph-bell'
}

/**
 * Get color for notification type
 * @param {string} type - Notification type
 * @returns {string} - Color class or hex color
 */
export const getNotificationColor = type => {
  const colors = {
    [NOTIFICATION_TYPES.ORDER_CREATED]: '#3498db',
    [NOTIFICATION_TYPES.ORDER_CONFIRMED]: '#27ae60',
    [NOTIFICATION_TYPES.ORDER_PREPARING]: '#f39c12',
    [NOTIFICATION_TYPES.ORDER_READY]: '#9b59b6',
    [NOTIFICATION_TYPES.ORDER_AVAILABLE]: '#17a2b8',
    [NOTIFICATION_TYPES.ORDER_ASSIGNED]: '#20c997',
    [NOTIFICATION_TYPES.DRIVER_EN_ROUTE]: '#3498db',
    [NOTIFICATION_TYPES.DRIVER_ARRIVED]: '#6f42c1',
    [NOTIFICATION_TYPES.ORDER_PICKED_UP]: '#1abc9c',
    [NOTIFICATION_TYPES.ORDER_IN_TRANSIT]: '#0dcaf0',
    [NOTIFICATION_TYPES.ORDER_DELIVERED]: '#2ecc71',
    [NOTIFICATION_TYPES.ORDER_CANCELLED]: '#e74c3c',
    [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: '#27ae60',
    [NOTIFICATION_TYPES.RATING_RECEIVED]: '#ffc107',
    [NOTIFICATION_TYPES.SYSTEM_ALERT]: '#7f8c8d',
    [NOTIFICATION_TYPES.LOCATION_UPDATE]: '#6c757d',
    [NOTIFICATION_TYPES.STATUS_UPDATE]: '#17a2b8'
  }
  return colors[type] || '#7f8c8d'
}

/**
 * Get priority color
 * @param {string} priority - Priority level
 * @returns {string} - Hex color
 */
export const getPriorityColor = priority => {
  const colors = {
    [PRIORITY_LEVELS.LOW]: '#6c757d',
    [PRIORITY_LEVELS.NORMAL]: '#17a2b8',
    [PRIORITY_LEVELS.HIGH]: '#fd7e14',
    [PRIORITY_LEVELS.URGENT]: '#dc3545'
  }
  return colors[priority] || '#17a2b8'
}

const notificationService = {
  // Notifications
  getNotifications,
  getUnreadCount,
  getDashboardStats,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  // Devices
  getDevices,
  registerDevice,
  updateDevice,
  deleteDevice,
  deactivateDevice,
  // Preferences
  getPreferences,
  updatePreferences,
  // Constants
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
  PRIORITY_LEVELS,
  PRIORITY_LABELS,
  CHANNEL_TYPES,
  CHANNEL_LABELS,
  DEVICE_TYPES,
  DEVICE_LABELS,
  UPDATE_TYPES,
  UPDATE_LABELS,
  // FCM Delivery Status
  DELIVERY_STATUS,
  DELIVERY_STATUS_LABELS,
  DELIVERY_STATUS_PROGRESS,
  DELIVERY_STATUS_ICONS,
  DELIVERY_STATUS_COLORS,
  FCM_MESSAGE_TYPES,
  getDeliveryStatusInfo,
  // Helpers
  getNotificationIcon,
  getNotificationColor,
  getPriorityColor
}

export default notificationService
